import { useQuery } from "@tanstack/react-query";

import type { Language } from "../types";

import { getSalesNarrativeContent } from "../api/getSalesNarrativeContent";
import { useContentfulClient } from "../ContentfulClientProvider";

export interface UseSalesNarrativeContentOptions {
    enabled?: boolean;
}

/**
 * How long to wait before treating the fetch as failed. React Query's `retry` only engages on a
 * rejected promise, so without this a request that stalls without ever settling leaves `isPending`
 * true forever — and every caller state that offers the expert a way out, or reports the fault, is
 * reached via `!isPending`. Long enough not to fire on a slow-but-working read, short enough that an
 * expert is not stranded for the call.
 */
export const FETCH_DEADLINE_MS = 10_000;

/**
 * Approved narrative copy for a SKU. `isError` is surfaced (unlike `useCallIntroFlowQuestions`, which
 * swallows it) because the caller has to tell "still loading" from "gave up" to pick a UI state — and
 * a stalled request resolves to `isError` rather than pending forever, so that distinction holds.
 */
export const useSalesNarrativeContent = (
    productSku: string,
    language: Language,
    options?: UseSalesNarrativeContentOptions,
) => {
    const contentfulClient = useContentfulClient();
    const { data, isPending, isError } = useQuery({
        queryKey: ["sales-narrative-content", productSku, language],
        queryFn: async () => {
            let deadline: ReturnType<typeof setTimeout> | undefined;
            try {
                return await Promise.race([
                    getSalesNarrativeContent(contentfulClient, productSku, language),
                    new Promise<never>((_, reject) => {
                        deadline = setTimeout(
                            () => reject(new Error(`Sales narrative content fetch exceeded ${FETCH_DEADLINE_MS}ms`)),
                            FETCH_DEADLINE_MS,
                        );
                    }),
                ]);
            } finally {
                clearTimeout(deadline);
            }
        },
        enabled: options?.enabled ?? true,
    });

    return { content: data ?? undefined, isPending, isError };
};
